import Dexie from 'dexie';
import { databaseManager } from '../../database/databaseManager';
import type {
  AppSettings,
  BackupData,
  Book,
  BookStatusHistory,
  CoverAsset,
  Goal,
  HomeHighlightContent,
  Note,
  Quote,
  ReadingProgressEntry,
  ReadingSession,
  Series
} from '../../domain/entities/types';
import type { EpubAsset, EpubReaderCache } from '../../database/epubStorageTypes';
import { createInitialSyncMetadata, persistLocalSyncBatch } from '../../sync/localOutbox';
import { getOrCreateDeviceId } from '../../sync/deviceId';
import { pendingCoverState } from '../../sync/coverSync';
import { syncExclusiveLock } from '../../sync/coordination';
import type { SyncEntityType, SyncMutationDraft, SyncOperation, SyncPayloadMap } from '../../sync/types';
import { nowInstant } from '../../utils/core';

const activeSyncUserId = (): string | null => databaseManager.getSnapshot().userId;

const mutation = <E extends SyncEntityType>(
  entityType: E,
  entityId: string,
  operation: SyncOperation,
  payload: SyncPayloadMap[E]
): SyncMutationDraft<E> => ({ entityType, entityId, operation, payload });

export const bookRepository = {
  get: (id: string) => databaseManager.runWithActiveDatabase((db) => db.books.get(id)),
  list: () => databaseManager.runWithActiveDatabase((db) => db.books.orderBy('updatedAt').reverse().toArray()),
  listByStatus: (status: Book['status']) => databaseManager.runWithActiveDatabase((db) => db.books.where('status').equals(status).reverse().sortBy('updatedAt')),
  put: (book: Book) => databaseManager.runWithActiveDatabase((db) => db.transaction(
    'rw', [db.books, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts], async () => {
      const operation = await db.books.get(book.id) ? 'update' : 'create';
      const result = await db.books.put(book);
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('book', book.id, operation, book)], book.updatedAt);
      return result;
    }
  ))
};

export const coverAssetRepository = {
  get: (id: string) => databaseManager.runWithActiveDatabase((db) => db.coverAssets.get(id)),
  put: (asset: CoverAsset) => databaseManager.runWithActiveDatabase((db) => db.coverAssets.put(asset))
};

export const sessionRepository = {
  get: (id: string) => databaseManager.runWithActiveDatabase((db) => db.sessions.get(id)),
  listByBook: (bookId: string) => databaseManager.runWithActiveDatabase((db) => db.sessions.where('bookId').equals(bookId).reverse().sortBy('startedAt')),
  listCompleted: () => databaseManager.runWithActiveDatabase((db) => db.sessions.where('status').equals('completed').toArray()),
  findOpen: () => databaseManager.runWithActiveDatabase((db) => db.sessions.where('status').anyOf('active', 'paused').first()),
  findOpenByBook: (bookId: string) => databaseManager.runWithActiveDatabase((db) => db.sessions.where('bookId').equals(bookId).filter(({ status }) => status !== 'completed').first())
};

export const progressRepository = {
  listByBook: (bookId: string) => databaseManager.runWithActiveDatabase((db) => db.progressEntries.where('bookId').equals(bookId).reverse().sortBy('recordedAt')),
  listAll: () => databaseManager.runWithActiveDatabase((db) => db.progressEntries.toArray())
};

export const statusHistoryRepository = {
  listByBook: (bookId: string) => databaseManager.runWithActiveDatabase((db) => db.statusHistory.where('bookId').equals(bookId).reverse().sortBy('changedAt'))
};

export const noteRepository = {
  listByBook: (bookId: string) => databaseManager.runWithActiveDatabase((db) => db.notes.where('bookId').equals(bookId).reverse().sortBy('createdAt')),
  put: (value: Note) => databaseManager.runWithActiveDatabase((db) => db.transaction(
    'rw', [db.notes, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts], async () => {
      const operation = await db.notes.get(value.id) ? 'update' : 'create';
      const result = await db.notes.put(value);
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('note', value.id, operation, value)], value.updatedAt);
      return result;
    }
  )),
  delete: (id: string) => databaseManager.runWithActiveDatabase((db) => db.transaction(
    'rw', [db.notes, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts], async () => {
      const note = await db.notes.get(id);
      if (!note) return;
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('note', note.id, 'delete', note)]);
      await db.notes.delete(id);
    }
  ))
};
export const quoteRepository = {
  listByBook: (bookId: string) => databaseManager.runWithActiveDatabase((db) => db.quotes.where('bookId').equals(bookId).reverse().sortBy('createdAt')),
  put: (value: Quote) => databaseManager.runWithActiveDatabase((db) => db.transaction(
    'rw', [db.quotes, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts], async () => {
      const operation = await db.quotes.get(value.id) ? 'update' : 'create';
      const result = await db.quotes.put(value);
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('quote', value.id, operation, value)], value.updatedAt);
      return result;
    }
  )),
  delete: (id: string) => databaseManager.runWithActiveDatabase((db) => db.transaction(
    'rw', [db.quotes, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts], async () => {
      const quote = await db.quotes.get(id);
      if (!quote) return;
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('quote', quote.id, 'delete', quote)]);
      await db.quotes.delete(id);
    }
  ))
};

export const seriesRepository = {
  list: () => databaseManager.runWithActiveDatabase((db) => db.series.orderBy('normalizedName').toArray()),
  get: (id: string) => databaseManager.runWithActiveDatabase((db) => db.series.get(id)),
  findByName: (normalizedName: string) => databaseManager.runWithActiveDatabase((db) => db.series.where('normalizedName').equals(normalizedName).first()),
  put: (series: Series) => databaseManager.runWithActiveDatabase((db) => db.transaction(
    'rw', [db.series, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts], async () => {
      const operation = await db.series.get(series.id) ? 'update' : 'create';
      const result = await db.series.put(series);
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('series', series.id, operation, series)], series.updatedAt);
      return result;
    }
  ))
};

export const goalRepository = {
  getByYear: (year: number) => databaseManager.runWithActiveDatabase((db) => db.goals.where('year').equals(year).first()),
  put: (goal: Goal) => databaseManager.runWithActiveDatabase((db) => db.transaction(
    'rw', [db.goals, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts], async () => {
      const operation = await db.goals.get(goal.id) ? 'update' : 'create';
      const result = await db.goals.put(goal);
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('goal', goal.id, operation, goal)], goal.updatedAt);
      return result;
    }
  ))
};

export const settingsRepository = {
  get: (): Promise<AppSettings | undefined> => databaseManager.runWithActiveDatabase(
    (db) => db.settings.get('app')
  ),
  async ensureInitialized(): Promise<AppSettings> {
    return databaseManager.runWithActiveDatabase((db) => db.transaction('rw', db.settings, async () => {
      const current = await db.settings.get('app');
      if (current) {
        if (typeof current.showHomeHighlight === 'boolean') return current;
        const legacy = current as AppSettings & { homeHighlight?: { enabled?: boolean } };
        const migrated: AppSettings = {
          ...current,
          showHomeHighlight: legacy.homeHighlight?.enabled ?? true
        };
        Reflect.deleteProperty(migrated, 'homeHighlight');
        await db.settings.put(migrated);
        return migrated;
      }
      const now = new Date().toISOString();
      const settings: AppSettings = {
        id: 'app', theme: 'light', locale: 'pt-BR',
        showHomeHighlight: true,
        createdAt: now, updatedAt: now
      };
      await db.settings.put(settings);
      return settings;
    }));
  },
  normalizeLegacyTheme: (theme: Exclude<AppSettings['theme'], 'system'>): Promise<AppSettings> =>
    databaseManager.runWithActiveDatabase((db) => db.transaction('rw', db.settings, async () => {
      const current = await db.settings.get('app');
      if (!current) {
        const now = nowInstant();
        const initialized: AppSettings = {
          id: 'app', theme, locale: 'pt-BR', showHomeHighlight: true,
          createdAt: now, updatedAt: now
        };
        await db.settings.put(initialized);
        return initialized;
      }
      if (current.theme !== 'system') return current;
      const normalized: AppSettings = { ...current, theme, updatedAt: nowInstant() };
      await db.settings.put(normalized);
      return normalized;
    })),
  put: (settings: AppSettings) => databaseManager.runWithActiveDatabase((db) => db.settings.put(settings)),
  putHomeHighlightPreference: (settings: AppSettings) => databaseManager.runWithActiveDatabase((db) => db.transaction(
    'rw', [db.settings, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts], async () => {
      const result = await db.settings.put(settings);
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('user_preference', 'app', 'update', {
        id: 'app',
        showHomeHighlight: settings.showHomeHighlight
      })], settings.updatedAt);
      return result;
    }
  )),
  putBibleBookOrderPreference: (settings: AppSettings) => databaseManager.runWithActiveDatabase((db) => db.transaction(
    'rw', [db.settings, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts], async () => {
      if (!settings.bibleBookOrder) throw new Error('O plano bíblico não foi informado.');
      const result = await db.settings.put(settings);
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('user_preference', 'bible_book_order', 'update', {
        id: 'bible_book_order',
        bookOrder: settings.bibleBookOrder
      })], settings.updatedAt);
      return result;
    }
  ))
};

export const homeHighlightContentRepository = {
  get: () => databaseManager.runWithActiveDatabase((db) => db.homeHighlightContent.get('home')),
  put: (content: HomeHighlightContent) => databaseManager.runWithActiveDatabase(
    (db) => db.homeHighlightContent.put(content)
  )
};

export const readingAggregateRepository = {
  async createBook(book: Book, statusEvent: BookStatusHistory, initialProgress: ReadingProgressEntry, cover?: CoverAsset, series?: Series): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', [
      db.books, db.statusHistory, db.progressEntries, db.coverAssets, db.series,
      db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts, db.syncCoverStates
    ], async () => {
      const userId = activeSyncUserId();
      if (series) await db.series.add(series);
      if (cover) await db.coverAssets.add(cover);
      if (cover && userId) await db.syncCoverStates.put(await Dexie.waitFor(pendingCoverState(userId, book.id, cover)));
      await db.books.add(book);
      await db.statusHistory.add(statusEvent);
      await db.progressEntries.add(initialProgress);
      const mutations: SyncMutationDraft[] = [
        ...(series ? [mutation('series', series.id, 'create', series)] : []),
        mutation('book', book.id, 'create', book),
        mutation('reading_progress_entry', initialProgress.id, 'create', initialProgress),
        mutation('book_status_history', statusEvent.id, 'create', statusEvent)
      ];
      await persistLocalSyncBatch(db, userId, mutations, book.updatedAt);
    }));
  },

  async completeEpubImport(
    book: Book,
    statusEvent: BookStatusHistory,
    initialProgress: ReadingProgressEntry,
    pendingAsset: EpubAsset,
    cover?: CoverAsset,
    series?: Series
  ): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', [
      db.epubAssets, db.books, db.statusHistory, db.progressEntries, db.coverAssets, db.series,
      db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts, db.syncCoverStates
    ], async () => {
      const asset = await db.epubAssets.get(book.id);
      if (!asset
        || asset.registrationState !== 'pending'
        || asset.sha256 !== pendingAsset.sha256
        || asset.bookId !== pendingAsset.bookId
        || book.progress.kind !== 'epub'
        || book.progress.documentSha256 !== pendingAsset.sha256
        || initialProgress.kind !== 'epub'
        || initialProgress.documentSha256 !== pendingAsset.sha256) {
        throw new Error('A importação EPUB pendente foi alterada. Importe o arquivo novamente.');
      }
      const sameDocumentAssets = (await db.epubAssets.toArray())
        .filter(({ sha256 }) => sha256 === pendingAsset.sha256);
      if (sameDocumentAssets.length !== 1 || sameDocumentAssets[0]?.bookId !== book.id) {
        throw new Error('Já existe outro registro local para este EPUB. Nenhum livro foi criado.');
      }
      if (await db.books.get(book.id)) {
        throw new Error('O EPUB pendente já foi associado a um livro.');
      }

      const userId = activeSyncUserId();
      if (series) await db.series.add(series);
      if (cover) await db.coverAssets.add(cover);
      if (cover && userId) await db.syncCoverStates.put(await Dexie.waitFor(pendingCoverState(userId, book.id, cover)));
      await db.books.add(book);
      await db.statusHistory.add(statusEvent);
      await db.progressEntries.add(initialProgress);
      await persistLocalSyncBatch(db, userId, [
        ...(series ? [mutation('series', series.id, 'create', series)] : []),
        mutation('book', book.id, 'create', book),
        mutation('reading_progress_entry', initialProgress.id, 'create', initialProgress),
        mutation('book_status_history', statusEvent.id, 'create', statusEvent)
      ], book.updatedAt);
      const completedAsset = {
        ...pendingAsset,
        registrationState: 'complete',
        updatedAt: book.updatedAt,
        blobBytes: await Dexie.waitFor(pendingAsset.blob.arrayBuffer())
      } as EpubAsset & { blobBytes: ArrayBuffer; blob?: Blob };
      Reflect.deleteProperty(completedAsset, 'blob');
      await db.epubAssets.put(completedAsset as EpubAsset);
    }));
  },

  async updateBook(
    book: Book,
    statusEvent?: BookStatusHistory,
    cover?: CoverAsset,
    oldCoverId?: string,
    progressEntry?: ReadingProgressEntry,
    supersededIds: string[] = [],
    expectedUpdatedAt?: string,
    series?: Series
  ): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', [
      db.books, db.statusHistory, db.coverAssets, db.progressEntries, db.series,
      db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts, db.syncCoverStates
    ], async () => {
      const userId = activeSyncUserId();
      const current = await db.books.get(book.id);
      if (!current || (expectedUpdatedAt && current.updatedAt !== expectedUpdatedAt)) {
        throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
      }
      if (series) await db.series.add(series);
      if (cover) await db.coverAssets.put(cover);
      if (cover && userId) await db.syncCoverStates.put(await Dexie.waitFor(pendingCoverState(userId, book.id, cover)));
      const supersededEntries = progressEntry
        ? (await Promise.all(supersededIds.map((id) => db.progressEntries.get(id)))).filter((entry): entry is ReadingProgressEntry => Boolean(entry))
        : [];
      if (progressEntry) {
        for (const id of supersededIds) await db.progressEntries.update(id, { supersededByEntryId: progressEntry.id });
        await db.progressEntries.add(progressEntry);
      }
      await db.books.put(book);
      if (statusEvent) await db.statusHistory.add(statusEvent);
      if (oldCoverId && oldCoverId !== cover?.id) {
        const stillUsed = await db.books.filter(({ coverAssetId }) => coverAssetId === oldCoverId).count();
        if (stillUsed === 0) {
          if (userId) {
            const oldState = await db.syncCoverStates.get(oldCoverId);
            const oldCover = await db.coverAssets.get(oldCoverId);
            const state = oldState ?? (oldCover
              ? await Dexie.waitFor(pendingCoverState(userId, book.id, oldCover))
              : undefined);
            if (state) await db.syncCoverStates.put({ ...state, status: 'orphaned', updatedAt: new Date().toISOString() });
          } else await db.coverAssets.delete(oldCoverId);
        }
      }
      const mutations: SyncMutationDraft[] = [
        ...(series ? [mutation('series', series.id, 'create', series)] : []),
        ...supersededEntries.map((entry) => mutation('reading_progress_entry', entry.id, 'update', {
          ...entry, supersededByEntryId: progressEntry!.id
        } as ReadingProgressEntry)),
        ...(progressEntry ? [mutation('reading_progress_entry', progressEntry.id, 'create', progressEntry)] : []),
        mutation('book', book.id, 'update', book),
        ...(statusEvent ? [mutation('book_status_history', statusEvent.id, 'create', statusEvent)] : [])
      ];
      await persistLocalSyncBatch(db, userId, mutations, book.updatedAt);
    }));
  },

  async commitEpubCheckpoint(
    book: Book,
    entry: ReadingProgressEntry,
    checkpoint: EpubReaderCache,
    expectedUpdatedAt: string
  ): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', [
      db.books, db.progressEntries, db.epubReaderCache,
      db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts
    ], async () => {
      const current = await db.books.get(book.id);
      if (!current || current.updatedAt !== expectedUpdatedAt) {
        throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
      }
      if (book.progress.kind !== 'epub' || entry.kind !== 'epub') {
        throw new Error('O checkpoint EPUB recebeu progresso incompatível.');
      }
      await db.epubReaderCache.put(checkpoint);
      await db.progressEntries.add(entry);
      await db.books.put(book);
      await persistLocalSyncBatch(db, activeSyncUserId(), [
        mutation('reading_progress_entry', entry.id, 'create', entry),
        mutation('book', book.id, 'update', book)
      ], book.updatedAt);
    }));
  },

  async commitProgress(
    book: Book,
    entry: ReadingProgressEntry,
    supersededIds: string[],
    statusEvent?: BookStatusHistory,
    completedSession?: ReadingSession,
    settings?: AppSettings,
    expectedUpdatedAt?: string,
    blockOpenSession = false,
    expectedSession?: Pick<ReadingSession, 'id' | 'status' | 'updatedAt'>
  ): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', [
      db.books, db.progressEntries, db.statusHistory, db.sessions, db.settings,
      db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts
    ], async () => {
      const current = await db.books.get(book.id);
      if (!current || (expectedUpdatedAt && current.updatedAt !== expectedUpdatedAt)) {
        throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
      }
      if (blockOpenSession) {
        const openSession = await db.sessions.where('bookId').equals(book.id).filter(({ status }) => status !== 'completed').first();
        if (openSession) throw new Error('Finalize a sessão atual antes de registrar uma leitura manual.');
      }
      if (completedSession && expectedSession) {
        const currentSession = await db.sessions.get(expectedSession.id);
        if (
          !currentSession
          || currentSession.status !== expectedSession.status
          || currentSession.updatedAt !== expectedSession.updatedAt
        ) throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
      }
      const supersededEntries = (await Promise.all(supersededIds.map((id) => db.progressEntries.get(id))))
        .filter((value): value is ReadingProgressEntry => Boolean(value));
      for (const id of supersededIds) await db.progressEntries.update(id, { supersededByEntryId: entry.id });
      await db.progressEntries.add(entry);
      await db.books.put(book);
      if (statusEvent) await db.statusHistory.add(statusEvent);
      if (completedSession) await db.sessions.put(completedSession);
      if (settings) await db.settings.put(settings);
      const mutations: SyncMutationDraft[] = [
        ...(completedSession?.status === 'completed'
          ? [mutation('reading_session', completedSession.id, 'create', completedSession)]
          : []),
        ...supersededEntries.map((previous) => mutation('reading_progress_entry', previous.id, 'update', {
          ...previous, supersededByEntryId: entry.id
        } as ReadingProgressEntry)),
        mutation('reading_progress_entry', entry.id, 'create', entry),
        mutation('book', book.id, 'update', book),
        ...(statusEvent ? [mutation('book_status_history', statusEvent.id, 'create', statusEvent)] : [])
      ];
      await persistLocalSyncBatch(db, activeSyncUserId(), mutations, book.updatedAt);
    }));
  },

  async startSession(session: ReadingSession, settings: AppSettings, expectedUpdatedAt: string): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', db.books, db.sessions, db.settings, async () => {
      const book = await db.books.get(session.bookId);
      if (!book || book.updatedAt !== expectedUpdatedAt) throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
      if (book.status !== 'reading' || book.progress.kind !== session.startProgress.kind) throw new Error('O livro não está compatível com uma nova sessão.');
      const openCount = await db.sessions.where('status').anyOf('active', 'paused').count();
      if (openCount > 0) throw new Error('Já existe uma sessão em andamento.');
      await db.sessions.add(session);
      await db.settings.put(settings);
    }));
  },

  async updateSession(
    session: ReadingSession,
    expected: Pick<ReadingSession, 'id' | 'status' | 'updatedAt'>,
    settings?: AppSettings
  ): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', db.sessions, db.settings, async () => {
      const current = await db.sessions.get(expected.id);
      if (!current || current.status !== expected.status || current.updatedAt !== expected.updatedAt) {
        throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
      }
      await db.sessions.put(session);
      if (settings) await db.settings.put(settings);
    }));
  },

  async discardSession(expected: Pick<ReadingSession, 'id' | 'status' | 'updatedAt'>): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', db.sessions, db.settings, async () => {
      const current = await db.sessions.get(expected.id);
      if (
        !current
        || current.status !== expected.status
        || current.updatedAt !== expected.updatedAt
      ) throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
      if (current.status === 'completed') throw new Error('Uma sessão concluída não pode ser descartada.');

      await db.sessions.delete(current.id);
      const settings = await db.settings.get('app');
      if (!settings) throw new Error('As configurações do aplicativo não foram encontradas.');
      if (settings.activeSessionId === current.id) delete settings.activeSessionId;
      settings.updatedAt = new Date(Math.max(Date.now(), Date.parse(settings.updatedAt) + 1)).toISOString();
      await db.settings.put(settings);
    }));
  },

  async reviseCompletedSession(
    book: Book,
    entry: ReadingProgressEntry,
    supersededEntryId: string,
    expectedBookUpdatedAt: string,
    expectedSession: Pick<ReadingSession, 'id' | 'status' | 'updatedAt'>,
    session: Extract<ReadingSession, { status: 'completed' }> | undefined,
    statusEvent?: BookStatusHistory
  ): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', [
      db.books, db.sessions, db.progressEntries, db.statusHistory,
      db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts
    ], async () => {
      const [currentBook, currentSession, currentEntry] = await Promise.all([
        db.books.get(book.id),
        db.sessions.get(expectedSession.id),
        db.progressEntries.get(supersededEntryId)
      ]);
      if (!currentBook || currentBook.updatedAt !== expectedBookUpdatedAt) {
        throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
      }
      if (
        !currentSession
        || currentSession.status !== 'completed'
        || expectedSession.status !== 'completed'
        || currentSession.updatedAt !== expectedSession.updatedAt
      ) throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
      if (!currentEntry || currentEntry.supersededByEntryId || currentEntry.sessionId !== currentSession.id) {
        throw new Error('O progresso desta sessão foi alterado em outra tela. Recarregue antes de continuar.');
      }
      const openSession = await db.sessions.where('bookId').equals(book.id).filter(({ status }) => status !== 'completed').first();
      if (openSession) throw new Error('Finalize a sessão atual antes de alterar uma sessão do histórico.');

      const mutations: SyncMutationDraft[] = [
        mutation('reading_session', currentSession.id, session ? 'update' : 'delete', session ?? currentSession),
        mutation('reading_progress_entry', currentEntry.id, 'update', {
          ...currentEntry, supersededByEntryId: entry.id
        } as ReadingProgressEntry),
        mutation('reading_progress_entry', entry.id, 'create', entry),
        mutation('book', book.id, 'update', book),
        ...(statusEvent ? [mutation('book_status_history', statusEvent.id, 'create', statusEvent)] : [])
      ];
      await persistLocalSyncBatch(db, activeSyncUserId(), mutations, book.updatedAt);
      await db.progressEntries.update(currentEntry.id, { supersededByEntryId: entry.id });
      await db.progressEntries.add(entry);
      await db.books.put(book);
      if (statusEvent) await db.statusHistory.add(statusEvent);
      if (session) await db.sessions.put(session);
      else await db.sessions.delete(currentSession.id);
    }));
  },

  async reviseProgressEntry(
    book: Book,
    entry: ReadingProgressEntry,
    expectedEntry: Pick<ReadingProgressEntry, 'id' | 'recordedAt'>,
    expectedBookUpdatedAt: string,
    statusEvent?: BookStatusHistory
  ): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', [
      db.books, db.progressEntries, db.statusHistory, db.sessions,
      db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts
    ], async () => {
      const [currentBook, currentEntry] = await Promise.all([
        db.books.get(book.id),
        db.progressEntries.get(expectedEntry.id)
      ]);
      if (!currentBook || currentBook.updatedAt !== expectedBookUpdatedAt) {
        throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
      }
      if (
        !currentEntry
        || currentEntry.bookId !== book.id
        || currentEntry.recordedAt !== expectedEntry.recordedAt
        || currentEntry.supersededByEntryId
      ) throw new Error('Esta leitura foi alterada em outra tela. Recarregue antes de continuar.');
      const openSession = await db.sessions.where('bookId').equals(book.id).filter(({ status }) => status !== 'completed').first();
      if (openSession) throw new Error('Finalize a sessão atual antes de alterar uma leitura do histórico.');

      await db.progressEntries.update(currentEntry.id, { supersededByEntryId: entry.id });
      await db.progressEntries.add(entry);
      await db.books.put(book);
      if (statusEvent) await db.statusHistory.add(statusEvent);
      await persistLocalSyncBatch(db, activeSyncUserId(), [
        mutation('reading_progress_entry', currentEntry.id, 'update', {
          ...currentEntry, supersededByEntryId: entry.id
        } as ReadingProgressEntry),
        mutation('reading_progress_entry', entry.id, 'create', entry),
        mutation('book', book.id, 'update', book),
        ...(statusEvent ? [mutation('book_status_history', statusEvent.id, 'create', statusEvent)] : [])
      ], book.updatedAt);
    }));
  },

  async deleteBook(bookId: string): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction(
      'rw',
      [
        db.books, db.coverAssets, db.sessions, db.progressEntries, db.statusHistory, db.notes, db.quotes, db.settings,
        db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts, db.syncCoverStates
      ],
      async () => {
        const userId = activeSyncUserId();
        const book = await db.books.get(bookId);
        const [sessions, progressEntries, statusHistory, notes, quotes] = await Promise.all([
          db.sessions.where('bookId').equals(bookId).toArray(),
          db.progressEntries.where('bookId').equals(bookId).toArray(),
          db.statusHistory.where('bookId').equals(bookId).toArray(),
          db.notes.where('bookId').equals(bookId).toArray(),
          db.quotes.where('bookId').equals(bookId).toArray()
        ]);
        const sessionIds = sessions.map(({ id }) => id);
        const mutations: SyncMutationDraft[] = [
          ...sessions.filter((session) => session.status === 'completed')
            .map((session) => mutation('reading_session', session.id, 'delete', session)),
          ...progressEntries.map((entry) => mutation('reading_progress_entry', entry.id, 'delete', entry)),
          ...statusHistory.map((event) => mutation('book_status_history', event.id, 'delete', event)),
          ...notes.map((note) => mutation('note', note.id, 'delete', note)),
          ...quotes.map((quote) => mutation('quote', quote.id, 'delete', quote)),
          ...(book ? [mutation('book', book.id, 'delete', book)] : [])
        ];
        await persistLocalSyncBatch(db, userId, mutations);
        await db.sessions.where('bookId').equals(bookId).delete();
        await db.progressEntries.where('bookId').equals(bookId).delete();
        await db.statusHistory.where('bookId').equals(bookId).delete();
        await db.notes.where('bookId').equals(bookId).delete();
        await db.quotes.where('bookId').equals(bookId).delete();
        await db.books.delete(bookId);
        if (book?.coverAssetId) {
          const stillUsed = await db.books.filter(({ coverAssetId }) => coverAssetId === book.coverAssetId).count();
          if (stillUsed === 0) {
            if (userId) {
              const oldState = await db.syncCoverStates.get(book.coverAssetId);
              const oldCover = await db.coverAssets.get(book.coverAssetId);
              const state = oldState ?? (oldCover
                ? await Dexie.waitFor(pendingCoverState(userId, book.id, oldCover))
                : undefined);
              if (state) await db.syncCoverStates.put({ ...state, status: 'orphaned', updatedAt: new Date().toISOString() });
            } else await db.coverAssets.delete(book.coverAssetId);
          }
        }
        const settings = await db.settings.get('app');
        if (settings?.activeSessionId && sessionIds.includes(settings.activeSessionId)) {
          delete settings.activeSessionId;
          settings.updatedAt = new Date().toISOString();
          await db.settings.put(settings);
        }
      }
    ));
  }
};

export const integrityRepository = {
  async recoverOpenSession(): Promise<ReadingSession | undefined> {
    return databaseManager.runWithActiveDatabase((db) => db.transaction('rw', db.sessions, db.settings, async () => {
      const settings = await db.settings.get('app');
      const referenced = settings?.activeSessionId ? await db.sessions.get(settings.activeSessionId) : undefined;
      if (referenced && referenced.status !== 'completed') return referenced;
      const fallback = await db.sessions.where('status').anyOf('active', 'paused').first();
      if (settings && settings.activeSessionId !== fallback?.id) {
        if (fallback) settings.activeSessionId = fallback.id;
        else delete settings.activeSessionId;
        settings.updatedAt = new Date().toISOString();
        await db.settings.put(settings);
      }
      return fallback;
    }));
  }
};

export const contentRepository = {
  async putNote(note: Note): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', [
      db.books, db.notes, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts
    ], async () => {
      if (!await db.books.get(note.bookId)) throw new Error('O livro desta anotação não existe mais.');
      const operation = await db.notes.get(note.id) ? 'update' : 'create';
      await db.notes.put(note);
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('note', note.id, operation, note)], note.updatedAt);
    }));
  },
  async putQuote(quote: Quote): Promise<void> {
    await databaseManager.runWithActiveDatabase((db) => db.transaction('rw', [
      db.books, db.quotes, db.syncOutbox, db.syncEntityStates, db.syncMetadata, db.syncConflicts
    ], async () => {
      if (!await db.books.get(quote.bookId)) throw new Error('O livro desta citação não existe mais.');
      const operation = await db.quotes.get(quote.id) ? 'update' : 'create';
      await db.quotes.put(quote);
      await persistLocalSyncBatch(db, activeSyncUserId(), [mutation('quote', quote.id, operation, quote)], quote.updatedAt);
    }));
  }
};

export const backupRepository = {
  async snapshot(): Promise<{ data: Omit<BackupData, 'coverAssets'>; covers: CoverAsset[] }> {
    return databaseManager.runWithActiveDatabase((db) => db.transaction(
      'r',
      [db.books, db.coverAssets, db.sessions, db.progressEntries, db.statusHistory, db.notes, db.quotes, db.series, db.goals, db.settings],
      async () => ({
        data: {
          books: await db.books.toArray(),
          sessions: await db.sessions.toArray(),
          progressEntries: await db.progressEntries.toArray(),
          statusHistory: await db.statusHistory.toArray(),
          notes: await db.notes.toArray(),
          quotes: await db.quotes.toArray(),
          series: await db.series.toArray(),
          goals: await db.goals.toArray(),
          settings: await db.settings.toArray()
        },
        covers: await db.coverAssets.toArray()
      })
    ));
  },
  async restore(data: BackupData, covers: CoverAsset[]): Promise<void> {
    await databaseManager.runWithActiveDatabase(async (db) => {
      const userId = activeSyncUserId();
      const resetMetadata = userId ? createInitialSyncMetadata(userId, getOrCreateDeviceId()) : undefined;
      const restore = () => db.transaction(
        'rw',
        [
          db.books, db.coverAssets, db.sessions, db.progressEntries, db.statusHistory, db.notes, db.quotes,
          db.series, db.goals, db.settings, db.syncOutbox, db.syncEntityStates, db.syncConflicts, db.syncMetadata,
          db.syncCoverStates, db.syncCoverConflicts
        ],
        async () => {
          const currentMetadata = userId ? await db.syncMetadata.get('sync') : undefined;
          if (currentMetadata && currentMetadata.bootstrapState !== 'not_started') {
            throw new Error('Esta biblioteca já está sincronizada com a nuvem. A restauração de backup sincronizado ainda não está disponível.');
          }
          await Promise.all([
            db.books.clear(), db.coverAssets.clear(), db.sessions.clear(), db.progressEntries.clear(),
            db.statusHistory.clear(), db.notes.clear(), db.quotes.clear(), db.series.clear(),
            db.goals.clear(), db.settings.clear(),
            ...(userId ? [
              db.syncOutbox.clear(), db.syncEntityStates.clear(), db.syncConflicts.clear(),
              db.syncMetadata.clear(), db.syncCoverStates.clear(), db.syncCoverConflicts.clear()
            ] : [])
          ]);
          await db.books.bulkAdd(data.books);
          await db.coverAssets.bulkAdd(covers);
          await db.sessions.bulkAdd(data.sessions);
          await db.progressEntries.bulkAdd(data.progressEntries);
          await db.statusHistory.bulkAdd(data.statusHistory);
          await db.notes.bulkAdd(data.notes);
          await db.quotes.bulkAdd(data.quotes);
          await db.series.bulkAdd(data.series);
          await db.goals.bulkAdd(data.goals);
          await db.settings.bulkAdd(data.settings);
          if (resetMetadata) await db.syncMetadata.add(resetMetadata);
        }
      );
      if (userId) await syncExclusiveLock.run(userId, restore);
      else await restore();
    });
  }
};
